import { CommonRootState } from '@common/types/common-root-state-type';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { NotifierService } from './NotifierService';
import { updateConnectedSockets } from '../reducers/ConnectedEnginesSlice';
import { MQTTSubscriber } from './MQTTSubscriber';

// Backward compatibility export
export const connectNotifier = createAsyncThunk<void, { accessToken: string }, { state: CommonRootState }>(
  'notifier/connectNotifier',
  async ({ accessToken }) => {
    const notifier = NotifierService.getInstance();
    notifier.connect(accessToken);
  }
);

// Connect as supported user for support agents
export const connectNotifierAsSupportedUser = createAsyncThunk<void, { supportedUserId: string }, { state: CommonRootState }>(
  'notifier/connectAsSupportedUser',
  async ({ supportedUserId }, thunkApi) => {
    const { dispatch } = thunkApi;
    
    try {
      // Import the backend API dynamically to avoid circular dependencies
      const { generateSupportedUserToken, getUserConnectedClients } = await import(/* webpackMode: "eager" */ '../reducers/backend-api/backend-api');
      
      // Generate token for the supported user
      const tokenResponse = await dispatch(generateSupportedUserToken.initiate({ supportedUserId })).unwrap();
      
      if (tokenResponse.success) {
        // Disconnect current connection first
        const notifier = NotifierService.getInstance();
        notifier.disconnect();
        
        // Clear current connected engines state
        dispatch(updateConnectedSockets([]));
        
        // Connect as the supported user
        notifier.connect(tokenResponse.accessToken);
        
        // Wait a bit for connection to establish, then fetch connected clients from EMQX
        setTimeout(async () => {
          try {
            const connectedClients = await dispatch(getUserConnectedClients.initiate({ userId: supportedUserId })).unwrap();
            dispatch(updateConnectedSockets(connectedClients));
          } catch (error) {
            console.error('Failed to fetch connected clients from EMQX:', error);
          }
        }, 2000); // Wait 2 seconds for connection to establish
      } else {
        throw new Error('Failed to generate supported user token');
      }
    } catch (error) {
      console.error('Failed to connect as supported user:', error);
      throw error;
    }
  }
);

export const disconnectNotifier = createAsyncThunk<void, void, { state: CommonRootState }>(
  'notifier/disconnect',
  async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const notifier = NotifierService.getInstance();
    notifier.disconnect();
    dispatch(updateConnectedSockets([]));
  }
);

/**
 * Reconnect the notifier if disconnected.
 * This is called by the alarm handler when the socket connection is lost.
 * It retrieves the stored access token and reconnects the MQTT client.
 */
export const reconnectNotifierIfNeeded = createAsyncThunk<boolean, void, { state: CommonRootState }>(
  'notifier/reconnectIfNeeded',
  async (_, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState();

    const notifier = NotifierService.getInstance();
    const mqttSubscriber = MQTTSubscriber.getInstance();

    // Check if already connected or reconnecting
    if (mqttSubscriber.isConnected()) {
      console.log('[Notifier] Already connected, skipping reconnect');
      return false;
    }

    // Get the stored access token from Redux state
    const accessToken = state.UserReducer?.accessToken;

    if (!accessToken) {
      console.log('[Notifier] No access token available for reconnection');
      return false;
    }

    console.log('[Notifier] Attempting to reconnect MQTT...');
    notifier.connect(accessToken);

    return true;
  }
);

export const notifierLogoutThunk = createAsyncThunk<void, string, { state: CommonRootState }>("notifier/logout", async (socketId, thunkApi) => {
  console.log('[Notifier] Logout operations not supported via MQTT, use HTTP API instead');
  // TODO: Replace with HTTP API call if needed
});

export const notifierSubscribeThunk = createAsyncThunk<void, { entityType: string; entityId: string }, { state: CommonRootState }>("notifierSubscribeThunk", async (params, thunkApi) => {
  const { entityType, entityId } = params;
  
  if (entityType === 'boards' || entityType === 'workspaces') {
    const mqttSubscriber = MQTTSubscriber.getInstance();
    
    if (mqttSubscriber.isConnected()) {
      mqttSubscriber.subscribeToEntity(entityType, entityId);
      console.log(`[Notifier] Subscribed to ${entityType}: ${entityId}`);
    } else {
      console.warn(`[Notifier] Cannot subscribe to ${entityType} ${entityId} - MQTT not connected`);
    }
  } else {
    console.log(`[Notifier] Entity type ${entityType} not supported for MQTT subscription`);
  }
});

export const identifySocketThunk = createAsyncThunk<
  void,
  string,
  { state: CommonRootState }
>("notifier/identify", async (socketId, thunkApi) => {
  console.log('[Notifier] Socket identification not applicable with MQTT');
  // Not applicable for MQTT
});

export const subscribeToDashboardThunk = createAsyncThunk<void, void, { state: CommonRootState }>("notifier/subscribe-dashboard", async (_, thunkApi) => {
  console.log('[Notifier] Dashboard subscription handled automatically via MQTT topics');
  // Dashboard topics are automatically subscribed if user has admin permissions
});

